import { isAxiosError } from 'axios';
import {
  type FirstStepFormData,
  type SecondStepFormData,
  type ThirdStepFormData,
} from '~/@types/MarisaTakesCare/form';
import type { ContractMarisaTakesCare } from '~/domain/MarisaTakesCare/contractMarisaTakesCare';
import type { ContractMarisaTakesCareResponseDTO } from '~/dto/MarisaTakesCare/contractMarisaTakesCare';
import { contractMarisaTakesCareDTOToContractMarisaTakesCare } from '~/mappers/MarisaTakesCare/contractMarisaTakesCare';
import apiAssegurou from '~/services/apiAssegurou';
import { dateToISO } from '~/utils/date';
import { formattedGender, formattedMaritalStatus } from '~/utils/form';
import {
  getPhoneInfos,
  getRawDocument,
  getRawZipcode,
  stringToCapitalCase,
} from '~/utils/string';

interface ContractData {
  firstStep: FirstStepFormData;
  secondStep: SecondStepFormData;
  thirdStep: ThirdStepFormData;
  userDocument: string | undefined;
  contractSeller: Checkout.ContractSeller;
}

export async function contractMarisaTakesCareUseCase(
  contract: ContractData,
  checkoutForm: Checkout.Form
): Promise<ContractMarisaTakesCare | null> {
  try {
    const { firstStep, secondStep, thirdStep, userDocument, contractSeller } =
      contract;
    const { creditCard } = checkoutForm;

    const [expiryDateMonth, expiryDateYear] = creditCard?.expiryDate.split(
      '/'
    ) as string[];

    const phoneInfos = getPhoneInfos(firstStep.cellphone);

    const contractSellerData = contractSeller
      ? {
          lojaCod: contractSeller.store?.code,
          vendedorCod: getRawDocument(contractSeller.seller?.document ?? ''),
        }
      : null;

    const response =
      await apiAssegurou.post<ContractMarisaTakesCareResponseDTO>(
        '/v1/Protecao/ContratarSeguroRedeMaisSaude',
        {
          proposta: {
            ...contractSellerData,
            dataVenda: new Date().toISOString(),
            tipoPagto: 'CC',
            vendaLoginCpf: userDocument ? getRawDocument(userDocument) : '',
            Segurado: {
              cpfCnpjSegurado: getRawDocument(secondStep.cpf),
              nomeSegurado: stringToCapitalCase(firstStep.fullName),
              dataNascimento: dateToISO(secondStep.birthDate),
              sexo: formattedGender[secondStep.gender],
              estadoCivil: formattedMaritalStatus[secondStep.maritalStatus],
              emailSegurado: firstStep.email,
              dddCelular: phoneInfos.ddd,
              telefoneCelular: phoneInfos.phone,
              enderecoCep: getRawZipcode(thirdStep.zipcode),
              endereco: thirdStep.street,
              enderecoNumero: thirdStep.number || 'S/N',
              enderecoComplemento: thirdStep.complement || 'S/C',
              enderecoBairro: thirdStep.neighborhood,
              enderecoCidade: thirdStep.city,
              enderecoUf: thirdStep.state,
            },
            dependentes: [],
            dadosPagamento: {
              numeroCartao: creditCard?.cardNumber.toString(),
              nomeCartao: stringToCapitalCase(creditCard?.holderName),
              mesValidadeCartao: expiryDateMonth,
              anoValidadeCartao: `20${expiryDateYear}`,
              cvv: creditCard?.CVV,
            },
          },
        }
      );

    return contractMarisaTakesCareDTOToContractMarisaTakesCare(response.data);
  } catch (err) {
    if (isAxiosError(err) && err.response?.data?.message) {
      throw {
        message: err.response?.data?.message,
      };
    }
    throw {
      message: null,
    };
  }
}
